import React, { useMemo, useState, useCallback, useEffect, useRef } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Alert,
  AlertTitle,
  Menu,
  MenuItem,
  TextField,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Tooltip,
  CircularProgress,
  Chip,
} from "@mui/material";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DownloadIcon from "@mui/icons-material/Download";
import { SelectedProductsRowItem } from "./SelectedProductsRowItem";
import { selectionFreshness } from "./forecast-freshness";
import { SelectedProductsOdooResponses } from "./SelectedProductsOdooResponses";
import { ProductDetailDrawer } from "./product-detail/ProductDetailDrawer";
import { StoreReplenishmentDetailDialog } from "./StoreReplenishmentDetailDialog";
import type {
  OdooAttemptResult,
  SelectedProduct,
} from "./SelectedProducts.types";
import MarginsService from "../../services/margins.service";
import ComprasService from "../../services/comprasService";
import { apiClient } from "../../services/apiClient";
import {
  buildSelectedProductsCsv,
  defaultCsvFilename,
  downloadCsv,
} from "../../pages/forecast/export-csv";
import {
  applyEconomicsOverlay,
  type LineEconomics,
} from "../../pages/forecast/apply-economics-overlay";
import { finalLineQty } from "../../pages/forecast/final-line-qty";

/** CalcResult shape returned by /api/forecasting/calc */
export type CalcResult = {
  id: string;
  suggestedPurchaseQty: number;
  forecastMethod?: string;
  details?: {
    horizonMonths: number;
    packSizeUsed: number;
    onHand: number;
    pipeline: number;
    roundingApplied: string;
    baseSuggested?: number;
    marginRatio?: number;
    unitsPerDay?: number;
    priorityScore?: number;
  };
  warnings?: string[];
  forecast_calculated_at?: string;
  /** B5.1 — números económicos canónicos del backend; el front los consume. */
  economics?: LineEconomics;
};

/** Samples-per-product shape from distribution seeding endpoint */
export type SamplesByProduct = Record<
  string,
  {
    pending: number;
    approved: number;
    total: number;
    /** Bloque B.4 — desagregación por source_type para columna "Muestras nuevas". */
    by_source?: {
      seeding: { pending: number; approved: number; total: number };
      reposicion: { pending: number; approved: number; total: number };
    };
  }
>;

export interface SelectedProductsDialogProps {
  open: boolean;
  products: SelectedProduct[];
  initialSuggestedTotal?: number | null;
  onClose: () => void;
  // Optional: parent callback to run after a successful PO creation when the dialog is closed
  onCloseAfterSuccess?: () => void;
  onMonthsChange?: (months: number) => void;
  // B4.1: horizonte derivado de la policy purchase_horizon_months por ForecastPage
  // (requerido — sin fallback ?? 2 hardcoded).
  initialMonths: number;
  // Default global del campo "Crecimiento proyectado" — policy
  // purchase_growth_pct (2026-06-10). Se re-aplica en cada apertura del dialog.
  initialGrowthPct?: number;
  selectionSyncLoading?: boolean;
  // Notifies parent when step changes (0-4). Used to trigger side-effects like data prefetch.
  onStepChange?: (step: number) => void;
  // Pre-computed forecast data from parent (single source of truth)
  calcResults?: CalcResult[];
  calcLoading?: boolean;
  samplesByProduct?: SamplesByProduct;
  globalMarginRatio?: number | null;
  onRecalculate?: () => void;
  // Deselecciona un producto desde el dialog (papelera por fila)
  onRemoveProduct?: (unifiedProductId: string) => void;
}

function getDisplayText(
  text: string | { en_US: string; es_MX: string } | null,
): string {
  if (!text) return "";
  if (typeof text === "string") return text;
  return text.es_MX || text.en_US || ""; // allow-hardcode
}

export const SelectedProductsDialog: React.FC<SelectedProductsDialogProps> = ({
  open,
  products,
  initialSuggestedTotal = null,
  onClose,
  onCloseAfterSuccess,
  onMonthsChange,
  initialMonths,
  initialGrowthPct = 0,
  selectionSyncLoading = false,
  onStepChange,
  calcResults: parentCalcResults,
  calcLoading: parentCalcLoading,
  samplesByProduct: parentSamplesByProduct,
  globalMarginRatio: parentGlobalMarginRatio,
  onRecalculate,
  onRemoveProduct,
}) => {
  // Whether parent provides pre-computed forecast data (must be first — used throughout)
  const hasParentCalc = parentCalcResults !== undefined;
  const count = products?.length ?? 0;
  const rows = useMemo(() => products ?? [], [products]);
  // months = single source of truth from parent (toolbar forecastMonths, cuyo
  // default viene de la policy purchase_horizon_months — B4.1). Sin fallback ?? 2.
  const months = initialMonths;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmTooHighOpen, setConfirmTooHighOpen] = useState(false);
  // Touch the value to satisfy strict lint when only used in JSX props later
  const _confirmTooHighOpenRead = confirmTooHighOpen;
  // Bloque B.8 — dialog de detalle por tienda (click en celda Reposición).
  const [storeReplenishmentProductId, setStoreReplenishmentProductId] =
    useState<string | null>(null);
  const [storeReplenishmentProductName, setStoreReplenishmentProductName] =
    useState<string>("");

  // Stepper: strict, non-clickable (FASE 2: simplified from 5 to 3 steps)
  const steps = ["Forecast de Compra", "Respuesta de Odoo"];
  const finalStepIndex = steps.length - 1; // 1
  const [activeStep, setActiveStep] = useState<number>(0);
  // Step 6: global historical margin per cost ratio — parent prop takes precedence
  const [internalGlobalMarginRatio, setGlobalMarginRatio] = useState<
    number | null
  >(null);
  const globalMarginRatio = hasParentCalc
    ? (parentGlobalMarginRatio ?? internalGlobalMarginRatio)
    : internalGlobalMarginRatio;

  // Effective pieces-per-pack for a product (user-persisted > original > 1)
  const getCurrentPurchaseCant = useCallback((p: SelectedProduct): number => {
    const baseVal =
      typeof p.purchaseCant === "number" && p.purchaseCant > 0
        ? p.purchaseCant
        : 1;
    const persisted =
      typeof p.purchaseCantUser === "number" && p.purchaseCantUser > 0
        ? p.purchaseCantUser
        : null;
    return persisted ?? baseVal;
  }, []);

  // Fase 3: detect products where effective purchaseCant is 1 but name suggests bulk packaging
  const [packWarningDismissed, setPackWarningDismissed] = useState(false);
  const suspiciousPackProducts = useMemo(() => {
    if (packWarningDismissed) return [];
    const pattern = /(?:WL|CAJA|PAQ|IMP)\s*\d+/i;
    return rows.filter((p) => {
      if (getCurrentPurchaseCant(p) !== 1) return false;
      const name = getDisplayText(p.unifiedName);
      return pattern.test(name);
    });
  }, [rows, getCurrentPurchaseCant, packWarningDismissed]);

  // Navigation handlers
  const handleNext = useCallback(() => {
    setActiveStep((prev) => {
      const next = Math.min(prev + 1, finalStepIndex);
      try {
        onStepChange && onStepChange(next);
      } catch {}
      return next;
    });
  }, [finalStepIndex]);
  const handleBack = useCallback(() => {
    setActiveStep((prev) => {
      const next = Math.max(prev - 1, 0);
      try {
        onStepChange && onStepChange(next);
      } catch {}
      return next;
    });
  }, []);

  const handleOpenMenu = (e: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(e.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  // Months menu helpers
  const monthsLabel = useMemo(
    () => (months === 1 ? "1 mes" : `${months} meses`),
    [months],
  );
  const menuOpen = Boolean(anchorEl);

  // Clear all data computed from step 1 onward (Calcular forecast+)
  const clearStep1Plus = useCallback(() => {
    setCalcResults([]);
    setSamplesByProduct({});
    setSuggestedOverrides({});
    setSuggestedPlusOverrides({});
    setCustomApprovedTotal(null);
    setIsEditingApproved(false);
    setAdjustedDiffs({});
    setAdjustDetailOpen(false);
    setAdjustDetail(null);
    setGlobalMarginRatio(null);
    autoCalcRanRef.current = false;
  }, []);

  const handleSelectMonths = (m: number) => {
    clearStep1Plus();
    if (onMonthsChange) onMonthsChange(m);
    handleCloseMenu();
  };

  // visibleRows se define tras calcResults con overlay de economics (B5.2).
  // Reset all local/session state when dialog closes
  const resetLocalState = useCallback(() => {
    setAnchorEl(null);
    setActiveStep(0);
    setCalcLoading(false);
    setCalcResults([]);
    setSamplesByProduct({});
    setSuggestedOverrides({});
    setSuggestedPlusOverrides({});
    setCustomApprovedTotal(null);
    setIsEditingApproved(false);
    setSaveError(null);
    setLastDraftId(null);
    setCreatedOdoo18PoId(null);
    setCreatedOdoo18PoIds([]);
    setOdooAttempts([]);
    setPackWarningDismissed(false);
    autoCalcRanRef.current = false;
  }, []);

  React.useEffect(() => {
    if (!open) {
      resetLocalState();
    }
  }, [open, resetLocalState]);

  // Row component moved to its own file to keep this file under 500 lines

  // Step 4: Calcular forecast — use parent props when provided, fallback to internal state
  const [internalCalcLoading, setCalcLoading] = useState(false);
  const calcLoading = hasParentCalc
    ? (parentCalcLoading ?? false)
    : internalCalcLoading;
  const [internalCalcResults, setCalcResults] = useState<CalcResult[]>([]);
  const calcResults: CalcResult[] = hasParentCalc
    ? (parentCalcResults ?? [])
    : internalCalcResults;

  // B5.2 — overlay de economics canónicos del backend (B5.1): costo/precio
  // unitario salen del calc result (única fuente de verdad), NO del ProductData
  // del front. Pantalla, gran total, PO y CSV usan el MISMO unitCost/sellingPrice
  // por SKU → se eliminan los "4 valores por producto" (auditoría F6).
  const visibleRows = useMemo(
    () =>
      applyEconomicsOverlay(
        rows,
        new Map(calcResults.map((r) => [r.id, r.economics])),
      ),
    [rows, calcResults],
  );

  // Samples from distribution system (pending + approved recommendations)
  const [internalSamplesByProduct, setSamplesByProduct] =
    useState<SamplesByProduct>({});
  const samplesByProduct: SamplesByProduct = hasParentCalc
    ? (parentSamplesByProduct ?? {})
    : internalSamplesByProduct;

  // Only seeding counts toward the PO qty — reposición and stock_alert
  // represent per-store redistribution of the SAME demand already captured
  // by baseSuggested (forecast × months − onHand). Adding them would
  // double-count.
  const seedingQtyFor = (pid: string) =>
    samplesByProduct[pid]?.by_source?.seeding?.total ?? 0;

  // Helper: Generate month names for column headers in Step 3
  // These should match the historical months (same month from 1 year ago)
  const getMonthNames = useCallback((horizonMonths: number): string[] => {
    const names: string[] = [];
    const now = new Date();
    const monthNames = [
      "Ene",
      "Feb",
      "Mar",
      "Abr",
      "May",
      "Jun",
      "Jul",
      "Ago",
      "Sep",
      "Oct",
      "Nov",
      "Dic",
    ];
    for (let i = 1; i <= horizonMonths; i++) {
      // Calculate the future month (for projection period)
      const futureDate = new Date(now.getFullYear(), now.getMonth() + i, 1);
      // But we want historical data from same month, 1 year ago
      names.push(monthNames[futureDate.getMonth()] || ""); // allow-hardcode
    }
    return names;
  }, []);
  const resultById = useMemo(
    () => new Map(calcResults.map((r) => [r.id, r])),
    [calcResults],
  );

  // Frescura por producto (CR-8/CR-9): cada fila señala su propio forecast
  // viejo; el pie cuenta cuántos y dice la edad del más viejo.
  const forecastFreshness = useMemo(
    () => selectionFreshness(calcResults.map((r) => r.forecast_calculated_at)),
    [calcResults],
  );
  // Step 5: overrides for suggested quantities
  const [suggestedOverrides, setSuggestedOverrides] = useState<
    Record<string, number>
  >({});
  const [suggestedPlusOverrides, setSuggestedPlusOverrides] = useState<
    Record<string, number>
  >({});
  // Crecimiento proyectado (%): se distribuye sobre productos elegibles
  // ponderado por (rotación × margen). Productos con override manual,
  // sin costo o sin margen quedan excluidos del reparto.
  const [growthPct, setGrowthPct] = useState<number>(initialGrowthPct);
  // El default global (policy purchase_growth_pct) puede llegar despues del
  // mount (fetch async en ForecastPage); se re-aplica SOLO en la transicion
  // closed->open — nunca pisa una edicion en curso del comprador.
  const prevOpenForGrowthRef = useRef(false);
  useEffect(() => {
    if (open && !prevOpenForGrowthRef.current) setGrowthPct(initialGrowthPct);
    prevOpenForGrowthRef.current = open;
  }, [open, initialGrowthPct]);
  const extraByProduct = useMemo<Record<string, number>>(() => {
    const out: Record<string, number> = {};
    if (!growthPct || growthPct <= 0) return out;
    type Eligible = {
      id: string;
      cost: number;
      weight: number;
    };
    const eligible: Eligible[] = [];
    let baselineCost = 0;
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d = r?.details as any;
      const demand = Math.max(0, Number(d?.baseSuggested) || 0);
      const sample = seedingQtyFor(p.unifiedProductId);
      const ovp = suggestedPlusOverrides[p.unifiedProductId];
      const baselineQty = finalLineQty({
        baseSuggested: demand,
        suggestedOverride: suggestedOverrides[p.unifiedProductId],
        suggestedPlusOverride: ovp,
        sampleQty: sample,
        extraQty: 0,
        packSize: getCurrentPurchaseCant(p),
      });
      const cost =
        typeof p.lastPurchaseCost === "number" && p.lastPurchaseCost > 0
          ? p.lastPurchaseCost
          : 0;
      if (cost > 0) baselineCost += baselineQty * cost;
      if (typeof ovp === "number") continue;
      if (typeof suggestedOverrides[p.unifiedProductId] === "number") continue;
      if (cost <= 0) continue;
      const sp =
        typeof p.sellingPrice === "number" && p.sellingPrice > 0
          ? p.sellingPrice
          : null;
      let marginRatio = 0;
      if (sp !== null && sp > cost) marginRatio = (sp - cost) / cost;
      else if (typeof globalMarginRatio === "number" && globalMarginRatio > 0)
        marginRatio = globalMarginRatio;
      if (marginRatio <= 0) continue;
      const rotation = baselineQty;
      if (rotation <= 0) continue;
      const weight = rotation * marginRatio;
      eligible.push({ id: p.unifiedProductId, cost, weight });
    }
    const targetExtraCost = (baselineCost * growthPct) / 100;
    const totalWeight = eligible.reduce((s, e) => s + e.weight, 0);
    if (targetExtraCost <= 0 || totalWeight <= 0) return out;
    for (const e of eligible) {
      const dollarShare = (e.weight / totalWeight) * targetExtraCost;
      const extraUnits = Math.round(dollarShare / e.cost);
      if (extraUnits > 0) out[e.id] = extraUnits;
    }
    return out;
  }, [
    growthPct,
    visibleRows,
    resultById,
    samplesByProduct,
    suggestedPlusOverrides,
    suggestedOverrides,
    globalMarginRatio,
    getCurrentPurchaseCant,
  ]);
  // FASE 2: Order rows - Step 0 (Forecast de Compra) sorts by monetary total descending
  const rowsForRender: SelectedProduct[] = useMemo(() => {
    // Step 0 (Forecast de Compra): monetary sorting by Total más muestras sugerido (descending)
    if (activeStep === 0) {
      const withTotals = visibleRows.map((p, idx) => {
        const r = resultById.get(p.unifiedProductId);
        const d: any = r?.details;
        const demand = d?.baseSuggested ?? 0;
        const sample = seedingQtyFor(p.unifiedProductId);
        const sugPlus = finalLineQty({
          baseSuggested: demand,
          suggestedOverride: suggestedOverrides[p.unifiedProductId],
          suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
          sampleQty: sample,
          extraQty: extraByProduct[p.unifiedProductId] ?? 0,
          packSize: getCurrentPurchaseCant(p),
        });
        const unitCostVal =
          typeof (p as any).lastPurchaseCost === "number"
            ? Math.max(0, (p as any).lastPurchaseCost)
            : 0;
        const totalPlus = Math.max(0, Number(sugPlus) || 0) * unitCostVal;
        return { p, key: totalPlus, idx };
      });
      withTotals.sort((a, b) => {
        if (b.key !== a.key) return b.key - a.key;
        return a.idx - b.idx;
      });
      return withTotals.map((x) => x.p);
    }
    return visibleRows;
  }, [
    activeStep,
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);
  const horizonAddedCount = useMemo(
    () => rows.filter((product) => product.addedByHorizon).length,
    [rows],
  );
  // Grand total for the PO (sum of monetary totals for rows with qty>0 and known unit cost)
  const step6GrandTotal = useMemo(() => {
    let sum = 0;
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d: any = r?.details;
      const demand = d?.baseSuggested ?? 0;
      const sample = seedingQtyFor(p.unifiedProductId);
      const sugPlus = finalLineQty({
        baseSuggested: demand,
        suggestedOverride: suggestedOverrides[p.unifiedProductId],
        suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
        sampleQty: sample,
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize: getCurrentPurchaseCant(p),
      });
      const qty = Math.max(0, Number(sugPlus) || 0);
      if (qty <= 0) continue;
      const unitCostVal =
        typeof (p as any).lastPurchaseCost === "number"
          ? Math.max(0, (p as any).lastPurchaseCost)
          : null;
      if (typeof unitCostVal === "number") sum += qty * unitCostVal;
    }
    return sum;
  }, [
    activeStep,
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);
  // Base margin and adjusted margin (+ penalty by horizon)
  const step6BaseMargin = useMemo(() => {
    let base = 0;
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d: any = r?.details;
      const demand = d?.baseSuggested ?? 0;
      const sample = seedingQtyFor(p.unifiedProductId);
      const sugPlus = finalLineQty({
        baseSuggested: demand,
        suggestedOverride: suggestedOverrides[p.unifiedProductId],
        suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
        sampleQty: sample,
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize: getCurrentPurchaseCant(p),
      });
      const qty = Math.max(0, Number(sugPlus) || 0);
      if (qty <= 0) continue;
      const unitCostVal =
        typeof (p as any).lastPurchaseCost === "number"
          ? Math.max(0, (p as any).lastPurchaseCost)
          : null;
      const priceVal =
        typeof (p as any).sellingPrice === "number"
          ? Math.max(0, (p as any).sellingPrice)
          : null;
      // B5.6 — margen real por producto (precio − costo), no ratio global.
      if (unitCostVal !== null && priceVal !== null)
        base += qty * (priceVal - unitCostVal);
    }
    return base;
  }, [
    activeStep,
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // B5.6 — ¿hay al menos una línea con unidades a comprar? El botón "Crear PO"
  // se habilita por esto (NO por el margen) para no depender del ratio global.
  const step6HasUnits = useMemo(() => {
    for (const p of visibleRows) {
      const d = resultById.get(p.unifiedProductId)?.details as
        | { baseSuggested?: number }
        | undefined;
      const qty = finalLineQty({
        baseSuggested: d?.baseSuggested ?? 0,
        suggestedOverride: suggestedOverrides[p.unifiedProductId],
        suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
        sampleQty: seedingQtyFor(p.unifiedProductId),
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize: getCurrentPurchaseCant(p),
      });
      if (qty > 0) return true;
    }
    return false;
  }, [
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // Step 5: editable approved total (clickable). Defaults to Total sugerido
  const [customApprovedTotal, setCustomApprovedTotal] = useState<number | null>(
    null,
  );
  const [isEditingApproved, setIsEditingApproved] = useState<boolean>(false);
  // Uncontrolled input for performance while editing "Monto aprobado"
  const approvedInputRef = React.useRef<HTMLInputElement | null>(null);
  // Track diffs after auto-adjust to highlight affected rows and show details
  const [adjustedDiffs, setAdjustedDiffs] = useState<
    Record<string, { from: number; to: number }>
  >({});
  const [adjustDetailOpen, setAdjustDetailOpen] = useState(false);
  const [adjustDetail, setAdjustDetail] = useState<{
    id: string;
    name: string;
    fromQty: number;
    toQty: number;
    unitCost: number | null;
  } | null>(null);

  // FASE 2: Aggregate monetary totals across visible rows (always calculated in step 0+)
  const sumTotals = useMemo(() => {
    let sumSuggested = 0;
    let sumPlus = 0;
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d = r?.details as any;
      const baseSug = Math.max(0, Number(d?.baseSuggested) || 0);

      const ov = suggestedOverrides[p.unifiedProductId];
      const sug = typeof ov === "number" ? ov : baseSug;

      // Suggested qty with overrides (includes samples from distribution system)
      const ovp = suggestedPlusOverrides[p.unifiedProductId];
      const sugPlus = finalLineQty({
        baseSuggested: baseSug,
        suggestedOverride: ov,
        suggestedPlusOverride: ovp,
        sampleQty: seedingQtyFor(p.unifiedProductId),
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize: getCurrentPurchaseCant(p),
      });

      // Unit cost from product row
      const unitCostVal =
        typeof (p as any).lastPurchaseCost === "number"
          ? Math.max(0, (p as any).lastPurchaseCost)
          : null;
      if (typeof unitCostVal === "number") {
        sumSuggested += Math.max(0, Number(sug) || 0) * unitCostVal;
        sumPlus += Math.max(0, Number(sugPlus) || 0) * unitCostVal;
      }
    }
    return { sumSuggested, sumPlus };
  }, [
    activeStep,
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // Approximate margin: revenue (list_price * qty) vs cost (lastPurchaseCost * qty)
  // Only includes products where BOTH sellingPrice and lastPurchaseCost are known
  const marginEstimate = useMemo(() => {
    let totalRevenue = 0;
    let totalCost = 0;
    let withBoth = 0;
    let missingPrice = 0;
    let missingCost = 0;
    let totalWithQty = 0;
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d = r?.details as any;
      const demand = d?.baseSuggested ?? 0;
      const sample = seedingQtyFor(p.unifiedProductId);
      const sugPlus = finalLineQty({
        baseSuggested: demand,
        suggestedOverride: suggestedOverrides[p.unifiedProductId],
        suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
        sampleQty: sample,
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize: getCurrentPurchaseCant(p),
      });
      const qty = Math.max(0, Number(sugPlus) || 0);
      if (qty <= 0) continue;
      totalWithQty++;
      const cost =
        typeof p.lastPurchaseCost === "number" && p.lastPurchaseCost > 0
          ? p.lastPurchaseCost
          : null;
      const price =
        typeof p.sellingPrice === "number" && p.sellingPrice > 0
          ? p.sellingPrice
          : null;
      if (cost === null) missingCost++;
      if (price === null) missingPrice++;
      // Only count towards margin when both values are present
      if (cost !== null && price !== null) {
        withBoth++;
        totalCost += qty * cost;
        totalRevenue += qty * price;
      }
    }
    const margin = totalRevenue - totalCost;
    const pct = totalRevenue > 0 ? (margin / totalRevenue) * 100 : null;
    return {
      totalRevenue,
      totalCost,
      margin,
      pct,
      withBoth,
      missingPrice,
      missingCost,
      totalWithQty,
    };
  }, [
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // Estimated time to sell: total suggested qty / total velocity (unitsPerDay)
  const sellTimeEstimate = useMemo(() => {
    let totalQty = 0;
    let totalVelocity = 0;
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d = r?.details as any;
      const demand = d?.baseSuggested ?? 0;
      const sample = seedingQtyFor(p.unifiedProductId);
      const sugPlus = finalLineQty({
        baseSuggested: demand,
        suggestedOverride: suggestedOverrides[p.unifiedProductId],
        suggestedPlusOverride: suggestedPlusOverrides[p.unifiedProductId],
        sampleQty: sample,
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize: getCurrentPurchaseCant(p),
      });
      const qty = Math.max(0, Number(sugPlus) || 0);
      if (qty <= 0) continue;
      const upd =
        typeof d?.unitsPerDay === "number" && d.unitsPerDay > 0
          ? d.unitsPerDay
          : 0;
      totalQty += qty;
      totalVelocity += upd;
    }
    if (totalVelocity <= 0 || totalQty <= 0) return null;
    const days = totalQty / totalVelocity;
    return days;
  }, [
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // Money format for totals: xxx,xxx.xx
  const fmtMoney = React.useCallback(
    (val: number) =>
      new Intl.NumberFormat("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(val),
    [],
  );
  // B4.1: el horizonte (months) es un control de la toolbar cuyo default viene de
  // la policy — NO un "ajuste del dialog". Antes `months !== 2` asumía 2 como
  // default hardcoded; los ajustes del dialog son solo los overrides de abajo.
  const hasDialogAdjustments =
    customApprovedTotal !== null ||
    Object.keys(suggestedOverrides).length > 0 ||
    Object.keys(suggestedPlusOverrides).length > 0 ||
    Object.keys(adjustedDiffs).length > 0;
  const canonicalSuggestedTotal =
    typeof initialSuggestedTotal === "number" && initialSuggestedTotal > 0
      ? initialSuggestedTotal
      : null;
  const effectiveSuggestedTotal = useMemo(() => {
    if (!hasDialogAdjustments && canonicalSuggestedTotal !== null) {
      return canonicalSuggestedTotal;
    }
    return sumTotals.sumPlus;
  }, [canonicalSuggestedTotal, hasDialogAdjustments, sumTotals.sumPlus]);
  // Approved total display (defaults to canonical supplier total while the dialog is untouched)
  const displayApprovedTotal = useMemo(
    () =>
      customApprovedTotal !== null
        ? customApprovedTotal
        : effectiveSuggestedTotal,
    [customApprovedTotal, effectiveSuggestedTotal],
  );

  const startApprovedEdit = useCallback(() => {
    setIsEditingApproved(true);
  }, []);
  const commitApprovedEdit = useCallback(() => {
    const raw = approvedInputRef.current?.value?.trim() ?? ""; // allow-hardcode
    if (!raw) {
      setIsEditingApproved(false);
      return;
    }
    const cleaned = raw.replace(/[$,\s]/g, "");
    const num = parseFloat(cleaned);
    if (isNaN(num) || num < 0) {
      setIsEditingApproved(false);
      return;
    }
    setCustomApprovedTotal(Number(num.toFixed(2)));
    setIsEditingApproved(false);
    // Automatically adjust quantities to the new approved total
    // This replaces the need for the "Ajustar al monto aprobado" button
    setTimeout(() => {
      if (activeStep === 2) {
        const target = Number(num.toFixed(2));
        const currentFromTotals = Number(sumTotals.sumPlus) || 0;
        const epsilon = 0.005;

        if (target > currentFromTotals + epsilon) {
          setConfirmTooHighOpen(true);
          return;
        }

        // Build row context for adjustment algorithm
        type RowCtx = {
          id: string;
          unitCost: number;
          packSize: number;
          baseSug: number;
          currPlus: number;
          success: number;
        };

        const rowsCtx: RowCtx[] = [];
        for (const p of visibleRows) {
          const r = resultById.get(p.unifiedProductId);
          const d: any = r?.details;
          const unitCost =
            typeof (p as any).lastPurchaseCost === "number"
              ? Math.max(0, (p as any).lastPurchaseCost)
              : 0;

          const canonicalBase = Math.max(0, Number(d?.baseSuggested) || 0);
          const ovSug = suggestedOverrides[p.unifiedProductId];
          const sampleVal = seedingQtyFor(p.unifiedProductId);
          const ovPlus = suggestedPlusOverrides[p.unifiedProductId];
          const packSize = Math.max(1, getCurrentPurchaseCant(p));
          const baseSug = finalLineQty({
            baseSuggested: canonicalBase,
            suggestedOverride: ovSug,
            sampleQty: sampleVal,
            extraQty: 0,
            packSize,
          });
          const currPlus = finalLineQty({
            baseSuggested: canonicalBase,
            suggestedOverride: ovSug,
            suggestedPlusOverride: ovPlus,
            sampleQty: sampleVal,
            extraQty: extraByProduct[p.unifiedProductId] ?? 0,
            packSize,
          });

          let success = 0;
          if (d && typeof d.priorityScore === "number")
            success = d.priorityScore;
          else if (d && typeof d.unitsPerDay === "number")
            success = d.unitsPerDay;
          else success = 0;

          rowsCtx.push({
            id: p.unifiedProductId,
            unitCost,
            packSize,
            baseSug,
            currPlus,
            success,
          });
        }

        const rowsWithCost = rowsCtx.filter((x) => x.unitCost > 0);
        if (rowsWithCost.length === 0) return;

        rowsWithCost.sort(
          (a, b) => a.success - b.success || a.unitCost - b.unitCost,
        );

        const proposed: Record<string, number> = Object.fromEntries(
          rowsCtx.map((r) => [r.id, r.currPlus]),
        );
        let needMoney = currentFromTotals - target;

        const shouldOptimize = Math.abs(needMoney) < 0.01 || needMoney === 0;

        if (shouldOptimize && Math.abs(needMoney) <= 0.01) {
          // Apply optimization/reduction logic here
          const candidatesForReduction = rowsWithCost.filter((r) => {
            const current = proposed[r.id] || 0;
            return current > r.baseSug;
          });

          const candidatesForIncrease = rowsWithCost.filter((r) => {
            const current = proposed[r.id] || 0;
            return current === r.baseSug;
          });

          if (
            candidatesForReduction.length > 0 &&
            candidatesForIncrease.length > 0
          ) {
            candidatesForReduction.sort((a, b) => a.success - b.success);
            candidatesForIncrease.sort((a, b) => b.success - a.success);

            const fromProduct = candidatesForReduction[0];
            const toProduct = candidatesForIncrease[0];

            if (
              fromProduct &&
              toProduct &&
              fromProduct.unitCost <= toProduct.unitCost
            ) {
              const fromCurrent = proposed[fromProduct.id] || 0;
              const toCurrent = proposed[toProduct.id] || 0;

              if (fromCurrent > fromProduct.baseSug) {
                proposed[fromProduct.id] = fromCurrent - 1;
                proposed[toProduct.id] = toCurrent + 1;
              }
            }
          } else if (needMoney > 0.01) {
            // Small reduction needed
            const sortedForReduction = rowsWithCost
              .filter((r) => (proposed[r.id] || 0) > r.baseSug)
              .sort((a, b) => a.success - b.success);

            for (const r of sortedForReduction) {
              if (needMoney <= 0.01) break;
              const curVal = proposed[r.id] || 0;
              const removableUnits = Math.max(0, curVal - r.baseSug);
              if (removableUnits <= 0 || r.unitCost <= 0) continue;
              const unitsToRemove = Math.min(
                removableUnits,
                Math.ceil(needMoney / r.unitCost),
              );
              if (unitsToRemove > 0) {
                proposed[r.id] = curVal - unitsToRemove;
                needMoney -= unitsToRemove * r.unitCost;
              }
            }
          }
        } else if (!shouldOptimize) {
          // Normal reduction algorithm
          for (const r of rowsWithCost) {
            if (needMoney <= 0.01) break;
            const curVal = proposed[r.id] || 0;
            const removableUnits = Math.max(0, curVal - r.baseSug);
            if (removableUnits <= 0 || r.unitCost <= 0) continue;
            const unitsToRemove = Math.min(
              removableUnits,
              Math.ceil(needMoney / r.unitCost),
            );
            if (unitsToRemove > 0) {
              proposed[r.id] = curVal - unitsToRemove;
              needMoney -= unitsToRemove * r.unitCost;
            }
          }

          if (needMoney > 0.01) {
            for (const r of rowsWithCost) {
              if (needMoney <= 0.01) break;
              const cur = proposed[r.id] || 0;
              if (cur <= 0 || r.unitCost <= 0) continue;
              const minPositive = Math.min(cur, r.packSize);
              const removableUnits = Math.max(0, cur - minPositive);
              if (removableUnits <= 0) continue;
              const unitsToRemove = Math.min(
                removableUnits,
                Math.ceil(needMoney / r.unitCost),
              );
              if (unitsToRemove > 0) {
                proposed[r.id] = cur - unitsToRemove;
                needMoney -= unitsToRemove * r.unitCost;
              }
            }
          }

          if (needMoney > 0.01) {
            for (const r of rowsWithCost) {
              if (needMoney <= 0.01) break;
              const cur = proposed[r.id] || 0;
              if (cur <= 0 || r.unitCost <= 0) continue;
              const removableUnits = cur;
              const unitsToRemove = Math.min(
                removableUnits,
                Math.ceil(needMoney / r.unitCost),
              );
              if (unitsToRemove > 0) {
                proposed[r.id] = cur - unitsToRemove;
                needMoney -= unitsToRemove * r.unitCost;
              }
            }
          }
        }

        // Apply the changes
        const diffs: Record<string, { from: number; to: number }> = {};
        for (const r of rowsCtx) {
          const from = r.currPlus;
          const to = proposed[r.id] ?? r.currPlus;
          if (from !== to) diffs[r.id] = { from, to };
        }
        setAdjustedDiffs(diffs);
        setSuggestedPlusOverrides(proposed);
      }
    }, 100); // Small delay to ensure state updates are processed
  }, [
    activeStep,
    sumTotals.sumPlus,
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);
  const cancelApprovedEdit = useCallback(() => {
    setIsEditingApproved(false);
  }, []);

  // Step 5: Incrementar cantidades cuando el monto aprobado es mayor
  const incrementToApproved = useCallback(() => {
    if (activeStep !== 2) return;
    const target = Number(displayApprovedTotal) || 0;
    const currentFromTotals = Number(sumTotals.sumPlus) || 0;
    const epsilon = 0.005;
    if (target <= currentFromTotals + epsilon) return;

    // Build row context with success score, cost, quantities
    type RowCtx = {
      id: string;
      unitCost: number;
      packSize: number;
      baseSug: number;
      currPlus: number;
      success: number; // mayor = más exitoso (inverso del algoritmo de reducción)
    };
    const rowsCtx: RowCtx[] = [];
    for (const p of visibleRows) {
      const r = resultById.get(p.unifiedProductId);
      const d: any = r?.details;
      const unitCost =
        typeof (p as any).lastPurchaseCost === "number"
          ? Math.max(0, (p as any).lastPurchaseCost)
          : 0;

      const canonicalBase = Math.max(0, Number(d?.baseSuggested) || 0);
      const ovSug = suggestedOverrides[p.unifiedProductId];
      const sampleVal = seedingQtyFor(p.unifiedProductId);
      const ovPlus = suggestedPlusOverrides[p.unifiedProductId];
      const packSize = Math.max(1, getCurrentPurchaseCant(p));
      const baseSug = finalLineQty({
        baseSuggested: canonicalBase,
        suggestedOverride: ovSug,
        sampleQty: sampleVal,
        extraQty: 0,
        packSize,
      });
      const currPlus = finalLineQty({
        baseSuggested: canonicalBase,
        suggestedOverride: ovSug,
        suggestedPlusOverride: ovPlus,
        sampleQty: sampleVal,
        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
        packSize,
      });

      // Éxito (prioridad) — mayor primero para incrementos
      let success = 0;
      if (d && typeof d.priorityScore === "number") success = d.priorityScore;
      else if (d && typeof d.unitsPerDay === "number") success = d.unitsPerDay;
      else success = 0;

      rowsCtx.push({
        id: p.unifiedProductId,
        unitCost,
        packSize,
        baseSug,
        currPlus,
        success,
      });
    }

    // Only rows with cost can contribute to monetary increment
    const rowsWithCost = rowsCtx.filter((x) => x.unitCost > 0);
    if (rowsWithCost.length === 0) return;

    // Sort by most successful first (opposite of reduction algorithm)
    rowsWithCost.sort(
      (a, b) => b.success - a.success || b.unitCost - a.unitCost,
    );

    const proposed: Record<string, number> = Object.fromEntries(
      rowsCtx.map((r) => [r.id, r.currPlus]),
    );
    let needMoney = target - currentFromTotals; // positive => need to add money

    // Phase 1: Distribute increment proportionally by success score, respecting pack sizes
    const totalSuccess = rowsWithCost.reduce(
      (sum, r) => sum + Math.max(0.1, r.success),
      0,
    ); // min success to avoid division by zero

    for (const r of rowsWithCost) {
      if (needMoney <= epsilon) break;

      const successWeight = Math.max(0.1, r.success) / totalSuccess;
      const targetIncrement = needMoney * successWeight;
      const unitsToAdd = Math.ceil(targetIncrement / r.unitCost);

      if (unitsToAdd > 0) {
        // Round to pack size
        const packsToAdd = Math.ceil(unitsToAdd / r.packSize);
        const actualUnitsToAdd = packsToAdd * r.packSize;
        const actualMoneyToAdd = actualUnitsToAdd * r.unitCost;

        if (actualMoneyToAdd <= needMoney + epsilon) {
          proposed[r.id] = (proposed[r.id] || 0) + actualUnitsToAdd;
          needMoney -= actualMoneyToAdd;
        }
      }
    }

    // Phase 2: If we still have money to distribute, add more packs to most successful products
    if (needMoney > epsilon) {
      for (const r of rowsWithCost) {
        if (needMoney <= epsilon) break;

        const packsToAdd = Math.max(
          1,
          Math.floor(needMoney / (r.unitCost * r.packSize)),
        );
        if (packsToAdd > 0) {
          const unitsToAdd = packsToAdd * r.packSize;
          const moneyToAdd = unitsToAdd * r.unitCost;

          if (moneyToAdd <= needMoney + epsilon) {
            proposed[r.id] = (proposed[r.id] || 0) + unitsToAdd;
            needMoney -= moneyToAdd;
          }
        }
      }
    }

    // Compute diffs for highlighting
    const diffs: Record<string, { from: number; to: number }> = {};
    for (const r of rowsCtx) {
      const from = r.currPlus;
      const to = proposed[r.id] ?? r.currPlus;
      if (from !== to) diffs[r.id] = { from, to };
    }
    setAdjustedDiffs(diffs);
    setSuggestedPlusOverrides(proposed);
    setConfirmTooHighOpen(false);
  }, [
    activeStep,
    displayApprovedTotal,
    sumTotals.sumPlus,
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // Map productId -> display name for details dialog
  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    rows.forEach((p) => {
      try {
        m.set(p.unifiedProductId, getDisplayText(p.unifiedName));
      } catch {
        m.set(p.unifiedProductId, String(p.unifiedProductId));
      }
    });
    return m;
  }, [rows]);

  // Paso 6: diálogo informativo para productos solo en Odoo 16
  const [odoo16InfoOpen, setOdoo16InfoOpen] = useState(false);
  const [odoo16InfoName, setOdoo16InfoName] = useState<string>("");
  const [productDetailOpen, setProductDetailOpen] = useState(false);
  const [productDetailId, setProductDetailId] = useState<string | null>(null);
  const [productDetailName, setProductDetailName] = useState<
    string | undefined
  >(undefined);
  const [samplesDetailOpen, setSamplesDetailOpen] = useState(false);
  const [samplesDetailName, setSamplesDetailName] = useState("");
  const [samplesDetailRows, setSamplesDetailRows] = useState<
    Array<{
      storeName: string;
      qty: number;
      status: string;
      abcClass: string | null;
      reason: string | null;
    }>
  >([]);
  const [samplesDetailLoading, setSamplesDetailLoading] = useState(false);
  // Paso 6: confirmación de creación en Odoo 18 para productos que solo existen en Odoo 16
  const [odoo16ConfirmOpen, setOdoo16ConfirmOpen] = useState(false);
  const [odoo16ConfirmList, setOdoo16ConfirmList] = useState<
    Array<{ id: string; name: string }>
  >([]);
  // Step 6: saving state and result
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [lastDraftId, setLastDraftId] = useState<string | null>(null);
  const [createdOdoo18PoId, setCreatedOdoo18PoId] = useState<
    number | string | null
  >(null);
  const [createdOdoo18PoIds, setCreatedOdoo18PoIds] = useState<
    Array<number | string>
  >([]);
  const [odooAttempts, setOdooAttempts] = useState<OdooAttemptResult[]>([]);

  // Ejecuta el POST para crear el borrador en backend (movido arriba para dependencias)
  const createPoDraft = useCallback(async () => {
    try {
      setSaving(true);
      setSaveError(null);
      setLastDraftId(null);
      setCreatedOdoo18PoId(null);
      setCreatedOdoo18PoIds([]);
      setOdooAttempts([]);
      const grandTotalNum = Number(step6GrandTotal.toFixed(2));
      const baseMarginNum = Number(step6BaseMargin.toFixed(2));
      const approvedTotalNum = Number(
        (customApprovedTotal ?? step6GrandTotal).toFixed(2),
      );
      const payloadLines = visibleRows
        .map((p) => {
          const base =
            resultById.get(p.unifiedProductId)?.suggestedPurchaseQty ?? 0;
          const r = resultById.get(p.unifiedProductId);
          const d: any = r?.details;
          const sugOverride = suggestedOverrides[p.unifiedProductId];
          const sugPlusOverride = suggestedPlusOverrides[p.unifiedProductId];
          const sampleQty = seedingQtyFor(p.unifiedProductId);

          // baseSuggested es qty_compra_final de C3: ya incluye muestras y el
          // pack del backend. finalLineQty comparte la cascada exacta con
          // tabla/totales/CSV y aplica el pack efectivo una sola vez.
          const baseSugFromBackend =
            d && typeof d.baseSuggested === "number" ? d.baseSuggested : base;
          const packSize = Math.max(1, getCurrentPurchaseCant(p));
          const effectiveQty = finalLineQty({
            baseSuggested: baseSugFromBackend,
            suggestedOverride: sugOverride,
            suggestedPlusOverride: sugPlusOverride,
            sampleQty,
            extraQty: extraByProduct[p.unifiedProductId] ?? 0,
            packSize,
          });
          const cost = Number(p.lastPurchaseCost ?? 0);

          const baseSug = d?.baseSuggested ?? 0;

          const packUsed = packSize;
          return {
            unifiedProductId: p.unifiedProductId,
            odoo18Id: p.odoo18Id ?? null,
            odoo16Id: p.odoo16Id ?? null,
            quantity: effectiveQty,
            unitCost: cost,
            note: null as string | null,
            details: {
              horizonMonths: months,
              packSizeUsed: packUsed,
              roundingApplied: d?.roundingApplied ?? null,
              suggestedBase: baseSug,
              suggestedFinal: effectiveQty,
              sampleQty,
              unitCost: cost,
              lineTotal: Number((effectiveQty * cost).toFixed(2)),
              marginRatio:
                typeof globalMarginRatio === "number"
                  ? globalMarginRatio
                  : null,
              overrides: {
                suggestedOverride:
                  typeof sugOverride === "number" ? sugOverride : null,
                suggestedPlusOverride:
                  typeof sugPlusOverride === "number" ? sugPlusOverride : null,
              },
            },
          };
        })
        .filter((ln) => ln.quantity > 0);
      const res = await ComprasService.createPoDraft({
        months,
        approvedTotal: approvedTotalNum,
        grandTotal: grandTotalNum,
        baseMargin: baseMarginNum,
        adjustedMargin: baseMarginNum,
        penaltyFraction: 0,
        lines: payloadLines,
      });
      const attempts = Array.isArray(res?.data?.odooAttempts)
        ? (res.data?.odooAttempts as OdooAttemptResult[])
        : [];
      const createdIds = Array.isArray(res?.data?.createdPoIds)
        ? res.data?.createdPoIds
        : [];
      setOdooAttempts(attempts);
      setCreatedOdoo18PoIds(createdIds);

      if (res?.status === "success" && res.data?.id) {
        setLastDraftId(res.data.id);
        const primaryId =
          res.data?.odoo18PoId ??
          (createdIds.length > 0 ? createdIds[0] : null);
        setCreatedOdoo18PoId(primaryId ?? null);
        setActiveStep(() => {
          try {
            onStepChange && onStepChange(finalStepIndex);
          } catch {}
          return finalStepIndex;
        });
      } else {
        setSaveError("No se pudo crear el borrador del PO");
        setActiveStep(() => {
          try {
            onStepChange && onStepChange(finalStepIndex);
          } catch {}
          return finalStepIndex;
        });
      }
    } catch (e: any) {
      setSaveError(e?.message || "Error creando el borrador del PO");
      setActiveStep(() => {
        try {
          onStepChange && onStepChange(finalStepIndex);
        } catch {}
        return finalStepIndex;
      });
    } finally {
      setSaving(false);
    }
  }, [
    visibleRows,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    months,
    globalMarginRatio,
    step6GrandTotal,
    step6BaseMargin,
    customApprovedTotal,
    finalStepIndex,
    onStepChange,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  const openCreatePO = useCallback(() => {
    // Nuevo proceso: ya no se requiere confirmación manual para productos sólo en Odoo 16.
    // Se procede directo; el backend se encarga de crear/reactivar automáticamente en Odoo 18.
    const only16 = visibleRows
      .filter((p) => p.existsInOdoo16 && !p.existsInOdoo18)
      .filter((p) => {
        const base =
          resultById.get(p.unifiedProductId)?.suggestedPurchaseQty ?? 0;
        const sugOverride = suggestedOverrides[p.unifiedProductId];
        const sugPlusOverride = suggestedPlusOverrides[p.unifiedProductId];
        const details = resultById.get(p.unifiedProductId)?.details;
        const effectiveQty = finalLineQty({
          baseSuggested: details?.baseSuggested ?? base,
          suggestedOverride: sugOverride,
          suggestedPlusOverride: sugPlusOverride,
          sampleQty: seedingQtyFor(p.unifiedProductId),
          extraQty: extraByProduct[p.unifiedProductId] ?? 0,
          packSize: getCurrentPurchaseCant(p),
        });
        if (effectiveQty <= 0) return false;
        const unitCost =
          typeof (p as any).lastPurchaseCost === "number"
            ? Math.max(0, (p as any).lastPurchaseCost)
            : null;
        return unitCost == null
          ? effectiveQty > 0
          : effectiveQty * unitCost > 0;
      })
      .map((p) => ({
        id: p.unifiedProductId,
        name: nameById.get(p.unifiedProductId) || p.unifiedProductId,
      }));
    // Guardar lista sólo para referencia (podría mostrarse en un panel informativo si se desea)
    if (only16.length > 0) {
      setOdoo16ConfirmList(only16);
      // Cerrar cualquier diálogo previo de confirmación obsoleto
      setOdoo16ConfirmOpen(false);
    }
    void createPoDraft();
  }, [
    visibleRows,
    nameById,
    resultById,
    suggestedOverrides,
    suggestedPlusOverrides,
    createPoDraft,
    samplesByProduct,
    extraByProduct,
    getCurrentPurchaseCant,
  ]);

  // Persist pack override to backend and update local product state
  const handleEditPack = useCallback(
    async (productId: string, value: number | null) => {
      try {
        await apiClient.patch(
          `/forecasting/product-mapping/${encodeURIComponent(productId)}/purchase-cant-user`,
          { value },
        );
      } catch (e) {
        console.error("Error saving purchaseCantUser:", e);
      }
      // Update local product so getCurrentPurchaseCant picks the new value
      const target = rows.find((p) => p.unifiedProductId === productId);
      if (target) target.purchaseCantUser = value;
    },
    [rows],
  );

  const handleCalcForecast = useCallback(async () => {
    try {
      setCalcLoading(true);
      // Build payload: productIds = visible rows (post step1 removals)
      const productIds = visibleRows.map((p) => p.unifiedProductId);
      const packOverrides: Array<{ id: string; value: number }> = [];

      // allSettled: una falla en samples NO debe descartar el response del forecast.
      const [forecastSettled, samplesSettled] = await Promise.allSettled([
        apiClient.post<{ status: string; data: any[] }>("/forecasting/calc", {
          productIds,
          months,
          packOverrides,
        }),
        apiClient.post<{
          data: Record<
            string,
            {
              pending: number;
              approved: number;
              total: number;
              by_source?: {
                seeding: { pending: number; approved: number; total: number };
                reposicion: { pending: number; approved: number; total: number };
              };
            }
          >;
        }>("/distribution/seeding/samples-by-products", { productIds }),
      ]);

      if (forecastSettled.status === "fulfilled") {
        const forecastJson = forecastSettled.value.data;
        if (
          forecastJson?.status === "success" &&
          Array.isArray(forecastJson.data)
        ) {
          setCalcResults(
            forecastJson.data.map((x: any) => ({
              id: String(x.id),
              suggestedPurchaseQty: Number(x.suggestedPurchaseQty || 0),
              forecastMethod: x.forecastMethod,
              details: x.details,
              warnings: x.warnings,
              forecast_calculated_at: x.forecast_calculated_at,
              economics: x.economics,
            })),
          );
        } else {
          setCalcResults([]);
        }
      } else {
        setCalcResults([]);
      }

      if (
        samplesSettled.status === "fulfilled" &&
        samplesSettled.value.data?.data
      ) {
        setSamplesByProduct(samplesSettled.value.data.data);
      } else {
        setSamplesByProduct({});
      }

      // Lazy-load global margin ratio for cost/margin columns
      if (globalMarginRatio === null) {
        try {
          const marginRes = await MarginsService.getGlobalMarginPerCostRatio(
            3,
            true,
          );
          if (marginRes && typeof marginRes.ratio === "number")
            setGlobalMarginRatio(marginRes.ratio);
        } catch {
          setGlobalMarginRatio(0);
        }
      }
    } catch {
      setCalcResults([]);
      setSamplesByProduct({});
    } finally {
      setCalcLoading(false);
    }
  }, [visibleRows, months]);

  const resetForecastAndRecalculate = useCallback(async () => {
    // Reset all custom values and state
    setCustomApprovedTotal(null);
    setIsEditingApproved(false);
    setAdjustedDiffs({});
    setSuggestedOverrides({});
    setSuggestedPlusOverrides({});

    // Allow auto-calculation to run again by resetting the ref
    autoCalcRanRef.current = false;

    // If parent provides a recalculate callback, delegate to parent
    if (hasParentCalc && onRecalculate) {
      onRecalculate();
      return;
    }

    // Trigger recalculation by calling handleCalcForecast
    try {
      await handleCalcForecast();
    } catch (error) {
      console.error("Error recalculating forecast:", error);
    }
  }, [handleCalcForecast, hasParentCalc, onRecalculate]);

  // FASE 2: Auto-run calculation when entering Step 0 (Forecast de Compra) if not already calculated
  // SKIP when parent provides pre-computed results (hasParentCalc)
  const autoCalcRanRef = React.useRef(false);
  React.useEffect(() => {
    // When parent provides calc data, skip internal auto-calc entirely
    if (hasParentCalc) return;

    if (!open) {
      console.log("🔍 Auto-calc: Dialog not open");
      return;
    }

    console.log("🔍 Auto-calc check:", {
      activeStep,
      selectionSyncLoading,
      calcLoading,
      calcResultsLength: calcResults.length,
      autoCalcRan: autoCalcRanRef.current,
      shouldTrigger:
        activeStep === 0 &&
        !selectionSyncLoading &&
        !calcLoading &&
        calcResults.length === 0 &&
        !autoCalcRanRef.current,
    });

    if (
      activeStep === 0 &&
      !selectionSyncLoading &&
      !calcLoading &&
      calcResults.length === 0 &&
      !autoCalcRanRef.current
    ) {
      console.log(
        "✅ Triggering auto-calculation for Step 0 (Forecast de Compra)",
      );
      autoCalcRanRef.current = true;
      void handleCalcForecast();
    }
  }, [
    open,
    activeStep,
    selectionSyncLoading,
    calcLoading,
    calcResults.length,
    handleCalcForecast,
    hasParentCalc,
  ]);

  // FASE 2: Reset approved amount when returning to Step 0 (Forecast de Compra)
  React.useEffect(() => {
    if (activeStep === 0) {
      setCustomApprovedTotal(null);
    }
  }, [activeStep]);

  // (removed) recoverable margin delta, now we show total margin instead in the dialog

  const handleCloseDialog = React.useCallback(() => {
    if (
      createdOdoo18PoIds.length > 0 ||
      createdOdoo18PoId ||
      lastDraftId ||
      odooAttempts.length > 0
    ) {
      try {
        onCloseAfterSuccess && onCloseAfterSuccess();
      } catch {}
      setCreatedOdoo18PoId(null);
      setCreatedOdoo18PoIds([]);
      setOdooAttempts([]);
      setLastDraftId(null);
      setSaveError(null);
    }
    onClose();
  }, [
    createdOdoo18PoIds.length,
    createdOdoo18PoId,
    lastDraftId,
    odooAttempts.length,
    onCloseAfterSuccess,
    onClose,
  ]);

  return (
    <Dialog
      open={open}
      onClose={handleCloseDialog}
      fullWidth
      maxWidth="lg"
      data-accent="teal"
      PaperProps={{
        sx: {
          bgcolor: "var(--surface)",
          borderRadius: "10px",
          fontSize: 13,
          "& .MuiTypography-root": { fontSize: 12.5, color: "var(--ink-2)" },
          "& .MuiTypography-h6": { fontSize: 18, lineHeight: 1.2 },
          "& .MuiTableCell-root": {
            fontSize: 12.5,
            color: "var(--ink-1)",
            borderColor: "var(--border)",
            fontVariantNumeric: "tabular-nums",
          },
          "& .MuiButton-root": { fontSize: 12.5, textTransform: "none" },
          "& .MuiInputBase-input": { fontSize: 12.5 },
          "& .MuiInputLabel-root": { fontSize: 12.5 },
          "& .MuiAlert-message": { fontSize: 12.5 },
          "& .MuiStepLabel-label": { fontSize: 12.5 },
          "& .MuiMenuItem-root": { fontSize: 12.5 },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: "20px",
          py: "14px",
          borderBottom: "1px solid var(--border)",
          bgcolor: "var(--surface)",
        }}
      >
        <Typography
          component="div"
          sx={{
            fontSize: 18,
            fontWeight: 600,
            letterSpacing: "-0.015em",
            color: "var(--ink-1)",
          }}
        >
          Productos seleccionados{" "}
          <Box
            component="span"
            sx={{
              color: "var(--ink-3)",
              fontWeight: 500,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            ({count})
          </Box>
        </Typography>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          bgcolor: "var(--bg)",
          borderColor: "var(--border)",
          px: "20px",
          py: "16px",
        }}
      >
        <Stack spacing={2} sx={{ mb: 2 }}>
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            sx={{
              "& .MuiStepLabel-label": {
                color: "var(--ink-3)",
                fontSize: 12.5,
              },
              "& .MuiStepLabel-label.Mui-active": {
                color: "var(--ink-1)",
                fontWeight: 600,
              },
              "& .MuiStepLabel-label.Mui-completed": {
                color: "var(--ink-2)",
              },
              "& .MuiStepIcon-root": { color: "var(--bg-2)" },
              "& .MuiStepIcon-root.Mui-active": { color: "var(--ink-1)" },
              "& .MuiStepIcon-root.Mui-completed": { color: "var(--accent)" },
              "& .MuiStepConnector-line": { borderColor: "var(--border)" },
            }}
          >
            {steps.map((label) => (
              <Step key={label} completed={false}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Stack>
        {/* Dead step 3 margin header removed — merged into 2-step wizard */}
        {createdOdoo18PoIds.length > 0 && activeStep < finalStepIndex && (
          <Alert
            severity="success"
            sx={{ mb: 1 }}
            data-testid="po-created-success"
          >
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography variant="body2" fontWeight={700} color="success.main">
                Órdenes creadas en Odoo 18
              </Typography>
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                flexWrap="wrap"
              >
                {createdOdoo18PoIds.map((id) => (
                  <Typography
                    key={String(id)}
                    variant="body2"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {String(id)}
                  </Typography>
                ))}
              </Stack>
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  try {
                    void navigator.clipboard.writeText(
                      createdOdoo18PoIds.map((id) => String(id)).join(", "),
                    );
                  } catch {}
                }}
                disableRipple
                sx={{ ml: 1 }}
              >
                Copiar IDs
              </Button>
            </Stack>
          </Alert>
        )}
        {/* Dead step 2 "Monto aprobado" block removed — merged into 2-step wizard */}
        {/* FASE 2: Pack warning alert - show in Step 0 (Forecast de Compra) */}
        {activeStep === 0 && suspiciousPackProducts.length > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberOutlinedIcon fontSize="small" />}
            onClose={() => setPackWarningDismissed(true)}
            sx={{ mb: 1.5 }}
          >
            <AlertTitle sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
              Piezas por paquete sospechosas
            </AlertTitle>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              {suspiciousPackProducts.length === 1
                ? "El siguiente producto tiene cantidad de compra = 1 pero su nombre sugiere un paquete mayor."
                : `Los siguientes ${suspiciousPackProducts.length} productos tienen cantidad de compra = 1 pero su nombre sugiere un paquete mayor.`}{" "}
              Puedes corregirlos desde el detalle del producto.
            </Typography>
            <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
              {suspiciousPackProducts.slice(0, 10).map((p) => (
                <li key={p.unifiedProductId}>
                  <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {getDisplayText(p.unifiedName)}
                  </Typography>
                </li>
              ))}
              {suspiciousPackProducts.length > 10 && (
                <li>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: "0.8rem", fontStyle: "italic" }}
                  >
                    ...y {suspiciousPackProducts.length - 10} mas
                  </Typography>
                </li>
              )}
            </Stack>
          </Alert>
        )}
        {/* FASE 2: Horizon selector - show in Step 0 only */}
        {activeStep === 0 && (
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              mb: 1.5,
              bgcolor: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              p: "10px 12px",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Typography
                variant="body2"
                sx={{ color: "var(--ink-3)", fontSize: 12.5 }}
              >
                Horizonte:
              </Typography>
              <Button
                size="small"
                variant="outlined"
                onClick={handleOpenMenu}
                disableRipple
                disabled={selectionSyncLoading}
                sx={{
                  transition: "none",
                  borderColor: "var(--border)",
                  color: "var(--ink-1)",
                  bgcolor: "var(--bg-2)",
                  fontWeight: 600,
                  "&:hover": {
                    bgcolor: "var(--border)",
                    borderColor: "var(--ink-3)",
                  },
                }}
              >
                {monthsLabel}
              </Button>
              <Menu
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleCloseMenu}
                keepMounted
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                  <MenuItem
                    key={m}
                    selected={m === months}
                    onClick={() => handleSelectMonths(m)}
                    disableRipple
                    sx={{ transition: "none" }}
                  >
                    {m === 1 ? "1 mes" : `${m} meses`}
                  </MenuItem>
                ))}
              </Menu>
              {/* FASE 2: Show loading indicator when calculating */}
              {(selectionSyncLoading || calcLoading) && (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <CircularProgress size={16} />
                  <Typography variant="caption" color="text.secondary">
                    {selectionSyncLoading
                      ? "Actualizando productos del horizonte..."
                      : "Calculando forecast..."}
                  </Typography>
                </Stack>
              )}
              {/* Info link for methodology */}
              <Chip
                size="small"
                label={
                  // B4.1: muestra el desglose por horizonte si hay productos
                  // añadidos por horizonte (sin atar a un umbral de 2 hardcoded).
                  horizonAddedCount > 0
                    ? `${count} productos (+${horizonAddedCount} por horizonte)`
                    : `${count} producto${count === 1 ? "" : "s"}`
                }
                variant="outlined"
                sx={{
                  borderColor: "var(--border)",
                  bgcolor: "var(--bg-2)",
                  color: "var(--ink-1)",
                  fontWeight: 500,
                  fontSize: 12,
                  height: 24,
                }}
              />
              {/* Approximate margin chip — only products with both cost and price */}
              {marginEstimate.pct !== null && (
                <Tooltip
                  title={`Basado en ${marginEstimate.withBoth} de ${marginEstimate.totalWithQty} productos con precio y costo.${marginEstimate.missingPrice > 0 ? ` Sin precio de lista: ${marginEstimate.missingPrice}.` : ""}${marginEstimate.missingCost > 0 ? ` Sin costo de compra: ${marginEstimate.missingCost}.` : ""} Ingreso: $ ${fmtMoney(marginEstimate.totalRevenue)} | Costo: $ ${fmtMoney(marginEstimate.totalCost)}`}
                >
                  <Chip
                    size="small"
                    label={`Margen ~${marginEstimate.pct.toFixed(1)}% ($ ${fmtMoney(marginEstimate.margin)})${marginEstimate.withBoth < marginEstimate.totalWithQty ? ` [${marginEstimate.withBoth}/${marginEstimate.totalWithQty}]` : ""}`}
                    variant="outlined"
                    sx={{
                      fontSize: 12,
                      height: 24,
                      borderColor:
                        marginEstimate.pct >= 30
                          ? "var(--pos)"
                          : marginEstimate.pct >= 15
                            ? "var(--warn)"
                            : "var(--neg)",
                      color:
                        marginEstimate.pct >= 30
                          ? "var(--pos)"
                          : marginEstimate.pct >= 15
                            ? "var(--warn)"
                            : "var(--neg)",
                      bgcolor:
                        marginEstimate.pct >= 30
                          ? "var(--pos-soft)"
                          : marginEstimate.pct >= 15
                            ? "var(--warn-soft)"
                            : "var(--neg-soft)",
                      fontWeight: 500,
                    }}
                  />
                </Tooltip>
              )}
              {/* Estimated sell time chip */}
              {sellTimeEstimate !== null && (
                <Tooltip title="Tiempo estimado para vender toda la mercancia basado en velocidad de ventas historica">
                  <Chip
                    size="small"
                    label={`Venta en ~${sellTimeEstimate < 30 ? `${Math.round(sellTimeEstimate)} dias` : sellTimeEstimate < 365 ? `${(sellTimeEstimate / 30).toFixed(1)} meses` : `${(sellTimeEstimate / 365).toFixed(1)} anios`}`}
                    variant="outlined"
                    sx={{
                      fontSize: 12,
                      height: 24,
                      borderColor:
                        sellTimeEstimate <= months * 30
                          ? "var(--pos)"
                          : sellTimeEstimate <= months * 45
                            ? "var(--warn)"
                            : "var(--neg)",
                      color:
                        sellTimeEstimate <= months * 30
                          ? "var(--pos)"
                          : sellTimeEstimate <= months * 45
                            ? "var(--warn)"
                            : "var(--neg)",
                      bgcolor:
                        sellTimeEstimate <= months * 30
                          ? "var(--pos-soft)"
                          : sellTimeEstimate <= months * 45
                            ? "var(--warn-soft)"
                            : "var(--neg-soft)",
                      fontWeight: 500,
                    }}
                  />
                </Tooltip>
              )}

              {/* E2E hooks */}
              {calcLoading && (
                <span data-testid="calc-loading" style={{ display: "none" }} />
              )}
              {calcResults.length > 0 && (
                <span data-testid="calc-complete" style={{ display: "none" }} />
              )}
            </Stack>
            <Stack
              direction="row"
              spacing={2}
              alignItems="baseline"
              flexWrap="wrap"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  sx={{ color: "var(--ink-3)", fontSize: 12.5 }}
                >
                  Crecimiento proyectado:
                </Typography>
                <TextField
                  size="small"
                  type="number"
                  value={growthPct}
                  onChange={(e) => {
                    const raw = e.target.value;
                    if (raw === "") {
                      setGrowthPct(0);
                      return;
                    }
                    const n = parseFloat(raw);
                    if (isNaN(n)) return;
                    setGrowthPct(Math.max(0, Math.min(100, n)));
                  }}
                  inputProps={{
                    min: 0,
                    max: 100,
                    step: 1,
                    style: { width: 60, textAlign: "right" },
                  }}
                  InputProps={{
                    endAdornment: (
                      <Typography
                        variant="body2"
                        sx={{ ml: 0.5, color: "var(--ink-3)", fontSize: 12.5 }}
                      >
                        %
                      </Typography>
                    ),
                    sx: {
                      bgcolor: "var(--bg-2)",
                      "& fieldset": { borderColor: "var(--border)" },
                      "&:hover fieldset": { borderColor: "var(--ink-3)" },
                      "&.Mui-focused fieldset": {
                        borderColor: "var(--accent)",
                      },
                    },
                  }}
                />
                {growthPct > 0 && (
                  <Tooltip title="Reparto ponderado por rotación × margen sobre productos elegibles. Productos con cantidad manual o sin costo/margen quedan excluidos.">
                    <Chip
                      size="small"
                      label={`+${Object.keys(extraByProduct).length} prods`}
                      variant="outlined"
                      sx={{
                        fontSize: 12,
                        height: 24,
                        borderColor: "var(--accent)",
                        color: "var(--accent)",
                        bgcolor: "var(--accent-soft)",
                        fontWeight: 500,
                      }}
                    />
                  </Tooltip>
                )}
              </Stack>
              <Stack direction="row" spacing={1} alignItems="baseline">
                <Typography
                  variant="body2"
                  sx={{ color: "var(--ink-3)", fontSize: 12.5 }}
                >
                  Monto total aproximado:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "var(--ink-1)",
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {`$ ${fmtMoney(effectiveSuggestedTotal)}`}
                </Typography>
              </Stack>
            </Stack>
          </Stack>
        )}
        {activeStep === finalStepIndex ? (
          <SelectedProductsOdooResponses
            attempts={odooAttempts}
            createdPoIds={createdOdoo18PoIds}
            grandTotal={step6GrandTotal}
            baseMargin={step6BaseMargin}
            retryDisabled={saving}
            errorMessage={saveError}
            {...(odooAttempts.length > 0 || Boolean(saveError)
              ? { onRetry: () => openCreatePO() }
              : {})}
          />
        ) : count === 0 ? (
          <Alert severity="info">No hay productos seleccionados.</Alert>
        ) : (
          <Stack
            spacing={2}
            sx={{
              bgcolor: "var(--surface)",
              border: "1px solid var(--border)",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <Table
              size="small"
              stickyHeader
              sx={{
                "& .MuiTableCell-stickyHeader": {
                  bgcolor: "var(--bg)",
                  color: "var(--ink-3)",
                  fontSize: 11,
                  fontWeight: 500,
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  borderBottom: "1px solid var(--border)",
                },
                "& tbody tr:hover": { bgcolor: "var(--bg)" },
                "& tbody td": { borderColor: "var(--border)" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell align="left" sx={{ width: 80 }}>
                    ERP
                  </TableCell>
                  <TableCell align="center" sx={{ width: 60 }}>
                    <Tooltip title="Metodo estadistico usado para estimar la demanda base del producto. SES=suavizamiento exponencial simple, HW=Holt-Winters, TSB=Teunter-Syntetos-Babai, CRO=Croston, SBA=Syntetos-Boylan Approximation y N/D=sin forecast utilizable.">
                      <span>Met.</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Nombre del producto</TableCell>
                  <Tooltip title="Tamaño de paquete (pack size) — múltiplo de compra al proveedor">
                    <TableCell align="right">Pack</TableCell>
                  </Tooltip>
                  <TableCell align="right">Inventario</TableCell>
                  <Tooltip title="Reposición sugerida = cantidad a COMPRAR (suma de déficits por tienda: seeding + reposicion). Deriva de la demanda mensual pronosticada («Pronóstico/mes» en el grid) + stock + horizonte. Click en la celda para ver detalle por tienda.">
                    <TableCell align="right">Reposición</TableCell>
                  </Tooltip>
                  <Tooltip title="Muestras nuevas — cantidad de unidades de seeding (tiendas sin historial del producto). Subset de la columna Reposición.">
                    <TableCell align="right">Muestras nuevas</TableCell>
                  </Tooltip>
                  <TableCell align="right">Costo unitario</TableCell>
                  <TableCell align="right">Margen</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell align="center" sx={{ width: 44 }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {rowsForRender.map((p) => {
                  const displayCant = getCurrentPurchaseCant(p);
                  const r = resultById.get(p.unifiedProductId);
                  const diff = adjustedDiffs[p.unifiedProductId];
                  // FASE 2: highlightSample logic no longer needed
                  return (
                    <SelectedProductsRowItem
                      key={p.unifiedProductId}
                      product={p}
                      activeStep={activeStep}
                      displayCant={displayCant}
                      getDisplayText={getDisplayText}
                      forecastMethod={r?.forecastMethod}
                      forecastCalculatedAt={r?.forecast_calculated_at}
                      highlightSampleRow={false}
                      highlightAdjusted={!!diff}
                      onEditPack={(val) => handleEditPack(p.unifiedProductId, val)}
                      onShowOdoo16Info={() => {
                        setOdoo16InfoName(getDisplayText(p.unifiedName));
                        setOdoo16InfoOpen(true);
                      }}
                      // FASE 2: suggestedQty always available (activeStep >= 0)
                      suggestedQty={(() => {
                        const d = r?.details;
                        // C3 canónica: ya incluye muestras y pack.
                        const base = d?.baseSuggested ?? 0;
                        const ov = suggestedOverrides[p.unifiedProductId];
                        return typeof ov === "number" ? ov : base;
                      })()}
                      suggestedPlusQty={finalLineQty({
                        baseSuggested: r?.details?.baseSuggested ?? 0,
                        suggestedOverride: suggestedOverrides[p.unifiedProductId],
                        suggestedPlusOverride:
                          suggestedPlusOverrides[p.unifiedProductId],
                        sampleQty: seedingQtyFor(p.unifiedProductId),
                        extraQty: extraByProduct[p.unifiedProductId] ?? 0,
                        packSize: getCurrentPurchaseCant(p),
                      })}
                      sampleQty={seedingQtyFor(p.unifiedProductId)}
                      newSamplesQty={
                        samplesByProduct[p.unifiedProductId]?.by_source
                          ?.seeding.total ?? 0
                      }
                      onSampleClick={() => {
                        // Bloque B.8 — abre el dialog de detalle por tienda
                        // (Reposición + Muestras nuevas) para el producto.
                        setStoreReplenishmentProductId(p.unifiedProductId);
                        setStoreReplenishmentProductName(
                          getDisplayText(p.unifiedName),
                        );
                      }}
                      step5Editable
                      onEditSuggested={(val: number | null) => {
                        setSuggestedOverrides((prev) => {
                          const next = { ...prev } as Record<string, number>;
                          if (val === null || isNaN(val as any))
                            delete next[p.unifiedProductId];
                          else
                            next[p.unifiedProductId] = Math.max(
                              0,
                              Math.min(1000000, Math.floor(val)),
                            );
                          return next;
                        });
                      }}
                      onEditSuggestedPlus={(val: number | null) => {
                        setSuggestedPlusOverrides((prev) => {
                          const next = { ...prev } as Record<string, number>;
                          if (val === null || isNaN(val as any))
                            delete next[p.unifiedProductId];
                          else
                            next[p.unifiedProductId] = Math.max(
                              0,
                              Math.min(1000000, Math.floor(val)),
                            );
                          return next;
                        });
                      }}
                      unitCost={
                        typeof (p as any).lastPurchaseCost === "number"
                          ? (p as any).lastPurchaseCost
                          : null
                      }
                      sellingPrice={
                        typeof (p as any).sellingPrice === "number"
                          ? (p as any).sellingPrice
                          : null
                      }
                      step6ShowCostsAndMargin={true}
                      onProductClick={() => {
                        setProductDetailId(p.unifiedProductId);
                        setProductDetailName(getDisplayText(p.unifiedName));
                        setProductDetailOpen(true);
                      }}
                      onRemove={
                        onRemoveProduct
                          ? () => onRemoveProduct(p.unifiedProductId)
                          : undefined
                      }
                    />
                  );
                })}
              </TableBody>
            </Table>

            {forecastFreshness && (
              <Typography
                variant="caption"
                sx={{
                  color:
                    forecastFreshness.staleCount > 0
                      ? "warning.main"
                      : "text.secondary",
                  textAlign: "right",
                  display: "block",
                }}
                data-testid="forecast-freshness-footer"
              >
                {forecastFreshness.staleCount > 0 ? "⚠ " : ""}
                {forecastFreshness.footer}
              </Typography>
            )}

            {/* Ajuste: detalle por renglón */}
            <Dialog
              open={adjustDetailOpen}
              onClose={() => setAdjustDetailOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Detalle del ajuste</DialogTitle>
              <DialogContent dividers>
                {adjustDetail ? (
                  <Stack spacing={1}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ wordBreak: "break-word" }}
                    >
                      {adjustDetail.name}
                    </Typography>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Cantidad anterior
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {adjustDetail.fromQty}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Cantidad nueva
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {adjustDetail.toQty}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Costo unitario
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace" }}
                      >
                        {typeof adjustDetail.unitCost === "number"
                          ? `$ ${fmtMoney(adjustDetail.unitCost)}`
                          : "—"}
                      </Typography>
                    </Stack>
                    {typeof adjustDetail.unitCost === "number" && (
                      <>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Total anterior
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {`$ ${fmtMoney(Math.max(0, adjustDetail.fromQty) * Math.max(0, adjustDetail.unitCost))}`}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Total nuevo
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {`$ ${fmtMoney(Math.max(0, adjustDetail.toQty) * Math.max(0, adjustDetail.unitCost))}`}
                          </Typography>
                        </Stack>
                        <Stack direction="row" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">
                            Diferencia
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ fontFamily: "monospace" }}
                          >
                            {`$ ${fmtMoney((Math.max(0, adjustDetail.toQty) - Math.max(0, adjustDetail.fromQty)) * Math.max(0, adjustDetail.unitCost))}`}
                          </Typography>
                        </Stack>
                      </>
                    )}
                  </Stack>
                ) : (
                  <Typography variant="body2">Sin datos.</Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  onClick={() => setAdjustDetailOpen(false)}
                  disableRipple
                >
                  Cerrar
                </Button>
              </DialogActions>
            </Dialog>

            {/* Paso 6: Información para productos solo en Odoo 16 */}
            <Dialog
              open={odoo16InfoOpen}
              onClose={() => setOdoo16InfoOpen(false)}
              maxWidth="xs"
              fullWidth
            >
              <DialogTitle>Producto en Odoo 16</DialogTitle>
              <DialogContent dividers>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {odoo16InfoName || "Este producto"} se creará en Odoo 18 para
                  su compra.
                </Typography>
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  onClick={() => setOdoo16InfoOpen(false)}
                  disableRipple
                >
                  Cerrar
                </Button>
              </DialogActions>
            </Dialog>

            {/* Paso 6: Confirmación de creación en Odoo 18 para productos que solo existen en Odoo 16 */}
            <Dialog
              open={odoo16ConfirmOpen}
              onClose={() => setOdoo16ConfirmOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Crear productos en Odoo 18</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={1}>
                  <Typography variant="body2">
                    Los siguientes productos solo existen en Odoo 16. Se crearán
                    en Odoo 18 para poder generar la orden de compra:
                  </Typography>
                  <Stack component="ul" spacing={0.25} sx={{ pl: 2, m: 0 }}>
                    {odoo16ConfirmList.map((x) => (
                      <Typography
                        key={x.id}
                        component="li"
                        variant="caption"
                        sx={{ wordBreak: "break-word" }}
                      >
                        {x.name}
                      </Typography>
                    ))}
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Esta acción prepara los productos para compra en Odoo 18.
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  onClick={() => setOdoo16ConfirmOpen(false)}
                  color="inherit"
                  disableRipple
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    setOdoo16ConfirmOpen(false);
                    void createPoDraft();
                  }}
                  disableRipple
                >
                  Confirmar y continuar
                </Button>
              </DialogActions>
            </Dialog>

            {/* Diálogo de confirmación para incrementar cantidades cuando el monto aprobado es mayor */}
            <Dialog
              open={confirmTooHighOpen}
              onClose={() => setConfirmTooHighOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Incrementar cantidades</DialogTitle>
              <DialogContent dividers>
                <Stack spacing={2}>
                  <Typography variant="body2">
                    El monto aprobado{" "}
                    <strong>${fmtMoney(displayApprovedTotal)}</strong> es mayor
                    que el total sugerido{" "}
                    <strong>${fmtMoney(effectiveSuggestedTotal)}</strong>.
                  </Typography>
                  <Typography variant="body2">
                    ¿Deseas incrementar las cantidades automáticamente para
                    alcanzar el monto aprobado?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    El sistema incrementará las cantidades priorizando los
                    productos más exitosos y respetando los tamaños de paquete.
                  </Typography>
                  <Typography
                    variant="body2"
                    color="warning.main"
                    sx={{ fontWeight: 600 }}
                  >
                    Diferencia a distribuir: $
                    {fmtMoney(
                      Math.max(
                        0,
                        displayApprovedTotal - effectiveSuggestedTotal,
                      ),
                    )}
                  </Typography>
                </Stack>
              </DialogContent>
              <DialogActions>
                <Button
                  size="small"
                  onClick={() => setConfirmTooHighOpen(false)}
                  color="inherit"
                  disableRipple
                >
                  Cancelar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={incrementToApproved}
                  disableRipple
                  data-testid="confirm-increment-btn"
                >
                  Incrementar cantidades
                </Button>
              </DialogActions>
            </Dialog>
          </Stack>
        )}
      </DialogContent>
      <DialogActions
        sx={{
          px: "20px",
          py: "12px",
          borderTop: "1px solid var(--border)",
          bgcolor: "var(--surface)",
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          sx={{ width: "100%" }}
          alignItems="center"
          justifyContent={
            activeStep === finalStepIndex &&
            (odooAttempts.length > 0 ||
              createdOdoo18PoIds.length > 0 ||
              Boolean(saveError))
              ? "center"
              : "space-between"
          }
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              size="small"
              variant="outlined"
              onClick={handleCloseDialog}
              disableRipple
              sx={{
                borderColor: "var(--border)",
                color: "var(--ink-2)",
                bgcolor: "transparent",
                fontWeight: 500,
                "&:hover": {
                  bgcolor: "var(--bg-2)",
                  borderColor: "var(--ink-3)",
                  color: "var(--ink-1)",
                },
              }}
            >
              Cerrar
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon sx={{ fontSize: 14 }} />}
              onClick={() => {
                const csv = buildSelectedProductsCsv({
                  products: visibleRows,
                  calcResults,
                  samplesByProduct,
                  suggestedOverrides,
                  suggestedPlusOverrides,
                  extraByProduct,
                  months,
                  growthPct,
                  globalMarginRatio,
                });
                downloadCsv(defaultCsvFilename(), csv);
              }}
              disableRipple
              data-testid="dialog-export-csv-btn"
              disabled={count === 0}
              sx={{
                borderColor: "var(--border)",
                color: "var(--ink-2)",
                bgcolor: "transparent",
                fontWeight: 500,
                "&:hover": {
                  bgcolor: "var(--bg-2)",
                  borderColor: "var(--ink-3)",
                  color: "var(--ink-1)",
                },
              }}
            >
              Exportar CSV
            </Button>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center">
            {activeStep === 0 ? (
              calcLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress
                    size={16}
                    sx={{ color: "var(--accent)" }}
                  />
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--ink-3)", fontSize: 12.5 }}
                  >
                    Calculando forecast...
                  </Typography>
                </Stack>
              ) : calcResults.length > 0 ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={openCreatePO}
                    disableRipple
                    data-testid="dialog-create-po-btn"
                    disabled={saving || !step6HasUnits}
                    sx={{
                      bgcolor: "var(--ink-1)",
                      color: "#fff",
                      fontWeight: 500,
                      boxShadow: "none",
                      "&:hover": { bgcolor: "#1a2236", boxShadow: "none" },
                      "&.Mui-disabled": {
                        bgcolor: "var(--bg-2)",
                        color: "var(--ink-3)",
                      },
                    }}
                  >
                    {saving
                      ? "Guardando…"
                      : !step6HasUnits
                        ? "Sin unidades para comprar"
                        : "Crear PO en Odoo"}
                  </Button>
                  {lastDraftId && (
                    <Typography
                      variant="caption"
                      sx={{ color: "var(--pos)", fontSize: 12 }}
                    >
                      Borrador creado: {lastDraftId}
                    </Typography>
                  )}
                  {saveError && (
                    <Typography
                      variant="caption"
                      sx={{ color: "var(--neg)", fontSize: 12 }}
                    >
                      {saveError}
                    </Typography>
                  )}
                </Stack>
              ) : null
            ) : activeStep === finalStepIndex ? (
              saveError ? (
                <Typography
                  variant="caption"
                  sx={{ color: "var(--neg)", fontSize: 12 }}
                >
                  {saveError}
                </Typography>
              ) : null
            ) : null}
          </Stack>
        </Stack>
      </DialogActions>
      <ProductDetailDrawer
        open={productDetailOpen}
        unifiedProductId={productDetailId}
        productName={productDetailName}
        onClose={() => setProductDetailOpen(false)}
      />
      <StoreReplenishmentDetailDialog
        open={storeReplenishmentProductId !== null}
        unifiedProductId={storeReplenishmentProductId}
        productName={storeReplenishmentProductName}
        onClose={() => {
          setStoreReplenishmentProductId(null);
          setStoreReplenishmentProductName("");
        }}
      />
      <Dialog
        open={samplesDetailOpen}
        onClose={() => setSamplesDetailOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>Detalle de muestras</DialogTitle>
        <DialogContent dividers>
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{ wordBreak: "break-word", mb: 1.5 }}
          >
            {samplesDetailName}
          </Typography>
          {samplesDetailLoading ? (
            <Stack alignItems="center" py={3}>
              <CircularProgress size={28} />
            </Stack>
          ) : samplesDetailRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Sin muestras programadas para este producto.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Tienda</TableCell>
                  <TableCell align="right">Cantidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>ABC</TableCell>
                  <TableCell>Razon</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {samplesDetailRows.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                        {row.storeName}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontWeight: 600 }}
                      >
                        {row.qty}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.8rem",
                          color:
                            row.status === "approved"
                              ? "success.main"
                              : "warning.main",
                        }}
                      >
                        {row.status === "approved" ? "Aprobada" : "Pendiente"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}
                      >
                        {row.abcClass || "—"}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "0.75rem",
                          maxWidth: 200,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.reason || "—"}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={() => setSamplesDetailOpen(false)}
            disableRipple
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default SelectedProductsDialog;
